/**
 * /logistica/configuracao — Configuracao de roteirizacao por unidade (B8).
 *
 * Por padrao a PLATAFORMA ja fornece roteirizacao GRATIS (OpenRouteService) a
 * todas as empresas — nao precisa configurar nada. Esta tela eh um OVERRIDE
 * opcional: usar conta PROPRIA (Google Maps premium ou ORS proprio, chave
 * write-only cifrada no backend) ou desligar o mapa real (mock).
 */
import React, { useCallback, useEffect, useState } from "react";
import { FiAlertCircle, FiInfo, FiMapPin } from "react-icons/fi";

import AsyncButton from "../../components/AsyncButton";
import EntityHeader from "../../components/EntityHeader";
import { useAuth } from "../../context/AuthContext";
import {
  atualizarConfigRoteirizacao,
  criarConfigRoteirizacao,
  listarConfigRoteirizacao,
  PROVIDERS_ROTA,
  PROVIDERS_ROTA_COM_CHAVE,
} from "../../services/logistica";
import { PageContainer } from "../../styles/shared";

import {
  Card,
  EmptyMsg,
  ErrorMsg,
  Field,
  Input,
  Pill,
  SectionTitle,
  Select,
} from "../Orcamentos/styles";


export default function ConfiguracaoRoteirizacao() {
  const { request, hasAllPermissions, isMaster } = useAuth();
  const podeEditar = isMaster || hasAllPermissions(["logistica:planejar"]);

  const [estado, setEstado] = useState({ loading: true, error: null });
  const [configId, setConfigId] = useState(null);
  const [unidadeId, setUnidadeId] = useState(null);
  const [provider, setProvider] = useState("openrouteservice");
  const [ativo, setAtivo] = useState(true);
  const [apiKeyPlain, setApiKeyPlain] = useState("");
  const [configurada, setConfigurada] = useState(false);

  const carregar = useCallback(async () => {
    setEstado({ loading: true, error: null });
    try {
      const cfgResp = await listarConfigRoteirizacao(request);
      const cfgs = Array.isArray(cfgResp) ? cfgResp : cfgResp?.results || [];
      if (cfgs.length > 0) {
        const c = cfgs[0];
        setConfigId(c.id);
        setUnidadeId(c.business_unit);
        setProvider(c.provider);
        setAtivo(c.ativo);
        setConfigurada(Boolean(c.api_key_configurada));
      } else {
        // sem config ainda: descobre a unidade pra criar
        const uniResp = await request("/api/unidades");
        const unis = Array.isArray(uniResp) ? uniResp : uniResp?.results || [];
        setUnidadeId(unis[0]?.id || null);
      }
      setEstado({ loading: false, error: null });
    } catch (err) {
      setEstado({ loading: false, error: err?.message || "Erro ao carregar." });
    }
  }, [request]);

  useEffect(() => { carregar(); }, [carregar]);

  const salvar = useCallback(async () => {
    const payload = { provider, ativo };
    if (apiKeyPlain.trim()) payload.api_key_plain = apiKeyPlain.trim();
    if (configId) {
      await atualizarConfigRoteirizacao(request, configId, payload);
    } else {
      if (!unidadeId) throw new Error("Unidade não encontrada.");
      await criarConfigRoteirizacao(request, { ...payload, business_unit: unidadeId });
    }
    setApiKeyPlain("");
    await carregar();
  }, [provider, ativo, apiKeyPlain, configId, unidadeId, request, carregar]);

  if (estado.loading) {
    return <PageContainer><EmptyMsg>Carregando...</EmptyMsg></PageContainer>;
  }

  return (
    <PageContainer>
      <EntityHeader
        title="Configuração de roteirização"
        subtitle="Por padrão a plataforma já fornece rota grátis — configure aqui só para usar conta própria"
      />

      {estado.error && (
        <ErrorMsg><FiAlertCircle size={14} /> {estado.error}</ErrorMsg>
      )}

      <div
        style={{
          display: "flex",
          gap: 10,
          alignItems: "flex-start",
          padding: "12px 14px",
          marginBottom: 16,
          borderRadius: 8,
          background: "var(--info-bg, rgba(37, 99, 235, 0.08))",
          border: "1px solid var(--info-border, rgba(37, 99, 235, 0.25))",
          fontSize: "0.85rem",
          lineHeight: 1.5,
        }}
      >
        <FiInfo size={18} style={{ flexShrink: 0, marginTop: 1, color: "var(--info, #2563eb)" }} />
        <span>
          A roteirização já vem <strong>grátis</strong> nesta plataforma
          (OpenRouteService) — você não precisa fazer nada. Use os campos abaixo
          apenas se quiser sua <strong>própria conta</strong>: Google Maps
          (premium, cobrança por uso) ou uma chave ORS sua.
        </span>
      </div>

      <Card>
        <SectionTitle><FiMapPin size={15} /> Provedor de rota</SectionTitle>

        <Field>
          Provedor
          <Select
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
            disabled={!podeEditar}
          >
            {PROVIDERS_ROTA.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </Select>
        </Field>

        {PROVIDERS_ROTA_COM_CHAVE.includes(provider) && (
          <Field>
            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {provider === "google_maps"
                ? "Chave da API Google Maps"
                : "Chave da API OpenRouteService (opcional)"}
              {configurada
                ? <Pill $tone="success">configurada</Pill>
                : <Pill $tone="warning">não configurada</Pill>}
            </span>
            <Input
              type="password"
              value={apiKeyPlain}
              onChange={(e) => setApiKeyPlain(e.target.value)}
              placeholder={
                configurada
                  ? "•••••••••• (deixe vazio p/ manter)"
                  : provider === "google_maps"
                    ? "cole a chave da sua conta Google"
                    : "deixe vazio p/ usar a chave gratis da plataforma"
              }
              disabled={!podeEditar}
              autoComplete="off"
            />
            <small style={{ opacity: 0.6, fontSize: "0.78rem" }}>
              A chave é cifrada no servidor e nunca exibida de volta.{" "}
              {provider === "google_maps" ? (
                <>
                  Cada empresa usa a própria conta Google (quota e cobrança
                  isoladas). Habilite a Directions API + Geocoding API no projeto
                  do Google Cloud.
                </>
              ) : (
                <>
                  Deixe <strong>vazio</strong> para usar a chave gratuita que a
                  plataforma já fornece. Preencha apenas se tiver uma conta ORS
                  própria (cadastro grátis em openrouteservice.org) e quiser quota
                  isolada.
                </>
              )}
            </small>
          </Field>
        )}

        <Field style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
          <input
            type="checkbox"
            checked={ativo}
            onChange={(e) => setAtivo(e.target.checked)}
            disabled={!podeEditar}
          />
          <span>Ativo</span>
        </Field>

        {podeEditar ? (
          <AsyncButton
            variant="primary"
            onClick={salvar}
            successMessage="Configuração salva"
          >
            Salvar
          </AsyncButton>
        ) : (
          <EmptyMsg style={{ padding: 8 }}>
            Você não tem permissão para editar — alterar a roteirização é de
            quem planeja a logística.
          </EmptyMsg>
        )}
      </Card>
    </PageContainer>
  );
}
